import React from 'react';

interface FractureAssessmentProps {
  data: {
    type: string;
    anatomic_location: string;
    side: string;
    bone_segment: string;
    open_closed: string;
    gustilo_classification: string;
    displacement: string;
    articular_extension: boolean;
  };
  onChange: (field: string, value: any) => void;
}

const TYPE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'completa', label: 'Completa' },
  { value: 'incompleta', label: 'Incompleta' },
  { value: 'conminuta', label: 'Conminuta' },
  { value: 'impactada', label: 'Impactada' },
  { value: 'patológica', label: 'Patológica' },
  { value: 'en tallo verde', label: 'En Tallo Verde' },
];

const LOCATION_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'fémur', label: 'Fémur' },
  { value: 'tibia', label: 'Tibia' },
  { value: 'peroné', label: 'Peroné' },
  { value: 'húmero', label: 'Húmero' },
  { value: 'radio', label: 'Radio' },
  { value: 'cúbito', label: 'Cúbito' },
  { value: 'tobillo', label: 'Tobillo' },
  { value: 'muñeca', label: 'Muñeca' },
  { value: 'cadera', label: 'Cadera' },
  { value: 'pelvis', label: 'Pelvis' },
  { value: 'columna', label: 'Columna' },
  { value: 'clavícula', label: 'Clavícula' },
  { value: 'mano', label: 'Mano' },
  { value: 'pie', label: 'Pie' },
  { value: 'costilla', label: 'Costilla' },
  { value: 'omóplato', label: 'Omóplato' },
  { value: 'rodilla', label: 'Rodilla' },
  { value: 'codo', label: 'Codo' },
  { value: 'hombro', label: 'Hombro' },
];

const SIDE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'izquierdo', label: 'Izquierdo' },
  { value: 'derecho', label: 'Derecho' },
  { value: 'bilateral', label: 'Bilateral' },
];

const BONE_SEGMENT_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'tercio proximal', label: 'Tercio Proximal' },
  { value: 'tercio medio', label: 'Tercio Medio' },
  { value: 'tercio distal', label: 'Tercio Distal' },
  { value: 'cabeza', label: 'Cabeza' },
  { value: 'cuello', label: 'Cuello' },
  { value: 'diáfisis', label: 'Diáfisis' },
  { value: 'cóndilo', label: 'Cóndilo' },
  { value: 'maleolar', label: 'Maleolar' },
  { value: 'supracondíleo', label: 'Supracondíleo' },
];

const OPEN_CLOSED_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'cerrada', label: 'Cerrada' },
  { value: 'abierta', label: 'Abierta' },
];

const GUSTILO_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'I', label: 'Grado I' },
  { value: 'II', label: 'Grado II' },
  { value: 'IIIA', label: 'Grado IIIA' },
  { value: 'IIIB', label: 'Grado IIIB' },
  { value: 'IIIC', label: 'Grado IIIC' },
];

const DISPLACEMENT_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'no desplazada', label: 'No Desplazada' },
  { value: 'mínimo', label: 'Mínimo' },
  { value: 'moderado', label: 'Moderado' },
  { value: 'severo', label: 'Severo' },
];

const FractureAssessment: React.FC<FractureAssessmentProps> = ({ data, onChange }) => {
  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">
          <i className="fa fa-bone me-1"></i>
          Evaluación de Fractura
        </h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={data.type}
                onChange={e => onChange('type', e.target.value)}
              >
                {TYPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Localización Anatómica</label>
              <select
                className="form-select"
                value={data.anatomic_location}
                onChange={e => onChange('anatomic_location', e.target.value)}
              >
                {LOCATION_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Lado</label>
              <select
                className="form-select"
                value={data.side}
                onChange={e => onChange('side', e.target.value)}
              >
                {SIDE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Segmento Óseo</label>
              <select
                className="form-select"
                value={data.bone_segment}
                onChange={e => onChange('bone_segment', e.target.value)}
              >
                {BONE_SEGMENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Abierta / Cerrada</label>
              <select
                className="form-select"
                value={data.open_closed}
                onChange={e => onChange('open_closed', e.target.value)}
              >
                {OPEN_CLOSED_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          {data.open_closed === 'abierta' && (
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Clasificación Gustilo</label>
                <select
                  className="form-select"
                  value={data.gustilo_classification}
                  onChange={e => onChange('gustilo_classification', e.target.value)}
                >
                  {GUSTILO_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Desplazamiento</label>
              <select
                className="form-select"
                value={data.displacement}
                onChange={e => onChange('displacement', e.target.value)}
              >
                {DISPLACEMENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="articular_extension"
                  checked={data.articular_extension}
                  onChange={e => onChange('articular_extension', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="articular_extension">
                  Extensión Articular
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FractureAssessment;
